import { Controller } from '@hotwired/stimulus'
import { haptics } from '~/common/helpers/haptics'
import './thread_nav_component.css'

const PADDING_TOP = 32
const PADDING_BOTTOM = 140
const INDICATOR_HEIGHT = 12 // 2px visual + 5px padding each side
const BTN_HEIGHT = 32 // icon-only sm button (~6px padding top/bottom + 18px icon)
const SCROLL_TOP_OFFSET = 8
const DRAG_THRESHOLD = 5 // px of movement before drag mode activates (touch + mouse)
const SCROLL_DURATION = 200 // ms — custom smooth-scroll duration; native 'smooth' is uncontrollable and slower

export default class extends Controller {
  static targets = [
    'rail',
    'tooltip',
    'tooltipAuthor',
    'tooltipContent',
    'prevBtn',
    'nextBtn',
  ]
  static values = {
    minMessages: { type: Number, default: 6 },
    previewLen: { type: Number, default: 300 },
    assistantName: { type: String, default: 'Simplero.Coach' },
  }

  connect() {
    const conversation = this.element.closest('.js-chat-bot-conversation')
    this.messagesContainer = conversation.querySelector(
      '.js-chat-bot-messages-container'
    )
    if (!this.messagesContainer) return

    this.messagesArea = this.messagesContainer.parentElement

    // Messages are appended to the inner .js-chat-bot-messages div,
    // so observe that for childList mutations (not the outer scroll container).
    this.messagesList = this.messagesContainer.querySelector(
      '.js-chat-bot-messages'
    )

    this._discoverMessages()
    this._buildIndicators()
    this._updateVisibility()

    this._boundScrollHandler = () => {
      if (this.rafId) cancelAnimationFrame(this.rafId)
      this.rafId = requestAnimationFrame(() => this._updateActiveIndicator())
    }
    this.messagesContainer.addEventListener(
      'scroll',
      this._boundScrollHandler,
      {
        passive: true,
      }
    )

    // scrollend fires when smooth scroll finishes — clear _jumping and sync final state.
    // Fallback timeout in _jumpToMessage handles browsers without scrollend support.
    this._boundScrollEndHandler = () => {
      clearTimeout(this._jumpingTimeout)
      this._jumping = false
      this._updateActiveIndicator()
    }
    this.messagesContainer.addEventListener(
      'scrollend',
      this._boundScrollEndHandler,
      {
        passive: true,
      }
    )

    this.mutationObserver = new MutationObserver(() => {
      this._discoverMessages()
      this._buildIndicators()
      this._updateVisibility()
      this._updateActiveIndicator()
    })
    this.mutationObserver.observe(this.messagesList || this.messagesContainer, {
      childList: true,
    })

    this.resizeObserver = new ResizeObserver(() => this._positionIndicators())
    this.resizeObserver.observe(this.railTarget)

    this._rail = this.railTarget
    this._boundTouchStart = this._handleTouchStart.bind(this)
    this._boundTouchMove = this._handleTouchMove.bind(this)
    this._boundTouchEnd = this._handleTouchEnd.bind(this)
    // passive: false on touchstart is required for iOS Safari — with passive: true iOS pre-commits
    // to a scroll gesture and ignores event.preventDefault() calls in touchmove.
    this._rail.addEventListener('touchstart', this._boundTouchStart, {
      passive: false,
    })
    this._rail.addEventListener('touchmove', this._boundTouchMove, {
      passive: false,
    })
    this._rail.addEventListener('touchend', this._boundTouchEnd, {
      passive: true,
    })
    this._rail.addEventListener('touchcancel', this._boundTouchEnd, {
      passive: true,
    })

    this._boundMouseDown = this._handleMouseDown.bind(this)
    this._boundMouseMove = this._handleMouseMove.bind(this)
    this._boundMouseUp = this._handleMouseUp.bind(this)
    this._rail.addEventListener('mousedown', this._boundMouseDown)

    this._updateActiveIndicator()
  }

  disconnect() {
    if (this.rafId) cancelAnimationFrame(this.rafId)
    cancelAnimationFrame(this._scrollRafId)
    this.messagesContainer?.removeEventListener(
      'scroll',
      this._boundScrollHandler
    )
    this.messagesContainer?.removeEventListener(
      'scrollend',
      this._boundScrollEndHandler
    )
    this.mutationObserver?.disconnect()
    this.resizeObserver?.disconnect()
    clearTimeout(this._hideNavBtnsTimeout)
    clearTimeout(this._jumpingTimeout)
    this._hideTooltip()
    this.messagesArea?.classList.remove('pr-10')
    this._rail?.removeEventListener('touchstart', this._boundTouchStart)
    this._rail?.removeEventListener('touchmove', this._boundTouchMove)
    this._rail?.removeEventListener('touchend', this._boundTouchEnd)
    this._rail?.removeEventListener('touchcancel', this._boundTouchEnd)
    this._rail?.removeEventListener('mousedown', this._boundMouseDown)
    window.removeEventListener('mousemove', this._boundMouseMove)
    window.removeEventListener('mouseup', this._boundMouseUp)
  }

  _discoverMessages() {
    const elements = this.messagesContainer.querySelectorAll(
      '.js-chat-bot-message'
    )
    this.messages = Array.from(elements).map((el) => {
      const isAssistant = el.classList.contains(
        'js-chat-bot-message--assistant'
      )

      return {
        id: el.id,
        element: el,
        role: isAssistant ? 'assistant' : 'user',
      }
    })
  }

  _buildIndicators() {
    const rail = this.railTarget
    rail
      .querySelectorAll('.js-chat-bot-thread-nav-indicator')
      .forEach((el) => el.remove())

    // Indicators are recreated without the active class — force the next
    // _updateActiveIndicator to re-apply the highlight.
    this._highlightedIndex = null

    this.messages.forEach((msg, index) => {
      const indicator = document.createElement('div')
      indicator.className = `chat-bot-thread-nav__indicator js-chat-bot-thread-nav-indicator chat-bot-thread-nav__indicator--${msg.role}`
      indicator.dataset.index = index

      indicator.addEventListener('mouseenter', () => {
        if (this._mouseDragging) return
        this._showTooltip(indicator, msg)
      })
      indicator.addEventListener('mouseleave', () => this._hideTooltip())
      indicator.addEventListener('click', () => {
        if (this._suppressClick) {
          this._suppressClick = false
          return
        }
        this._scrollToMessage(msg)
      })

      rail.appendChild(indicator)
    })

    this._positionIndicators()
  }

  _positionIndicators() {
    const rail = this.railTarget
    const indicators = rail.querySelectorAll(
      '.js-chat-bot-thread-nav-indicator'
    )
    if (indicators.length === 0) return

    const railHeight = rail.offsetHeight
    const availableHeight = railHeight - PADDING_TOP - PADDING_BOTTOM
    if (availableHeight <= 0) return

    // Cluster tightly (INDICATOR_HEIGHT spacing = no gap between hit areas),
    // falling back to tighter spacing only if there are too many indicators to fit.
    const spacing =
      indicators.length > 1
        ? Math.min(INDICATOR_HEIGHT, availableHeight / (indicators.length - 1))
        : 0
    const clusterHeight = (indicators.length - 1) * spacing
    const startY = PADDING_TOP + (availableHeight - clusterHeight) / 2

    indicators.forEach((ind, i) => {
      ind.style.top = `${startY + i * spacing}px`
    })

    // Position nav buttons flanking the indicator cluster.
    // Use BTN_HEIGHT constant — offsetHeight returns 0 when element starts hidden.
    const lastTop = startY + (indicators.length - 1) * spacing
    this.prevBtnTarget.style.top = `${startY - BTN_HEIGHT}px`
    this.nextBtnTarget.style.top = `${lastTop + INDICATOR_HEIGHT}px`
  }

  _updateVisibility() {
    const shouldShow = this.messages.length >= this.minMessagesValue
    this.element.hidden = !shouldShow

    // Use padding on the parent (messagesArea) rather than margin on messagesContainer.
    // A margin on a flex child can escape the parent's overflow clip on iOS Safari,
    // causing page-level horizontal scroll. Padding stays inside the box model.
    this.messagesArea?.classList.toggle('pr-10', shouldShow)
  }

  _syncIndicators(index) {
    const hasPrev = this._prevUserMessageIndex(index) != null
    const hasNext = this._nextUserMessageIndex(index) != null

    this.prevBtnTarget.disabled = !hasPrev
    this.prevBtnTarget.title = hasPrev ? 'Previous message' : 'Already at top'
    this.nextBtnTarget.disabled = !hasNext
    this.nextBtnTarget.title = hasNext ? 'Next message' : 'Already at bottom'

    this.railTarget
      .querySelectorAll('.js-chat-bot-thread-nav-indicator')
      .forEach((ind, i) => {
        ind.classList.toggle(
          'chat-bot-thread-nav__indicator--active',
          i === index
        )
      })
  }

  // The highlight is driven solely by scroll position — including during the
  // programmatic scroll of a chevron jump — so it tracks the scroll smoothly
  // instead of snapping on click and re-correcting at scrollend.
  _updateActiveIndicator() {
    if (this.messages.length === 0) return

    const containerRect = this.messagesContainer.getBoundingClientRect()
    const viewTopY = containerRect.top + SCROLL_TOP_OFFSET

    let closestIndex = 0
    let closestDistance = Infinity

    this.messages.forEach((msg, index) => {
      if (!msg.element?.isConnected) return
      const rect = msg.element.getBoundingClientRect()
      const distance = Math.abs(rect.top - viewTopY)
      if (distance < closestDistance) {
        closestDistance = distance
        closestIndex = index
      }
    })

    // While a chevron jump is in flight, keep activeIndex pinned to the target
    // so rapid clicks still step from the right place.
    if (!this._jumping) this.activeIndex = closestIndex

    if (this._highlightedIndex === closestIndex) return
    this._highlightedIndex = closestIndex
    this._syncIndicators(closestIndex)
  }

  _showTooltip(indicator, msg) {
    const tooltip = this.tooltipTarget

    // Extract preview text from DOM.
    // For user messages wrapped in TruncatedHtml, the hidden revealable toggle div
    // contains the full untruncated text (without the "Read more" button text).
    const contentEl = msg.element?.querySelector(`#content_${msg.id}`)
    const fullTextEl =
      contentEl?.querySelector('[data-revealable-target="toggle"].hidden') ??
      contentEl
    const text = fullTextEl?.textContent?.trim() || ''
    const preview =
      text.length > this.previewLenValue
        ? text.substring(0, this.previewLenValue) + '…'
        : text
    const isAssistant = msg.role === 'assistant'
    this.tooltipAuthorTarget.hidden = !isAssistant
    this.tooltipAuthorTarget.textContent = isAssistant
      ? this.assistantNameValue
      : ''
    this.tooltipContentTarget.textContent = preview || '…'

    tooltip.classList.remove('hidden')

    const indicatorRect = indicator.getBoundingClientRect()
    const containerRect = this.element.getBoundingClientRect()
    const tooltipHeight = tooltip.offsetHeight

    let top =
      indicatorRect.top -
      containerRect.top -
      tooltipHeight / 2 +
      indicatorRect.height / 2

    top = Math.max(8, Math.min(top, containerRect.height - tooltipHeight - 8))
    tooltip.style.top = `${top}px`
    tooltip.style.right = `${this.railTarget.offsetWidth + 8}px`
  }

  showNavBtns() {
    clearTimeout(this._hideNavBtnsTimeout)
    this.prevBtnTarget.classList.remove(
      'opacity-0',
      'pointer-events-none',
      '-translate-y-1'
    )
    this.nextBtnTarget.classList.remove(
      'opacity-0',
      'pointer-events-none',
      'translate-y-1'
    )
  }

  hideNavBtns() {
    // Debounce to prevent spurious mouseleave during programmatic scroll
    this._hideNavBtnsTimeout = setTimeout(() => {
      this.prevBtnTarget.classList.add(
        'opacity-0',
        'pointer-events-none',
        '-translate-y-1'
      )
      this.nextBtnTarget.classList.add(
        'opacity-0',
        'pointer-events-none',
        'translate-y-1'
      )
    }, 200)
  }

  prevMessage() {
    const index = this._prevUserMessageIndex(this.activeIndex)
    if (index != null) this._jumpToMessage(index)
  }

  nextMessage() {
    const index = this._nextUserMessageIndex(this.activeIndex)
    if (index != null) this._jumpToMessage(index)
  }

  // Arrows step between user messages only. User messages render collapsed,
  // so landing on one keeps that question and its response visible together.
  _prevUserMessageIndex(from) {
    for (let i = from - 1; i >= 0; i--) {
      if (this.messages[i].role === 'user') return i
    }
    return null
  }

  _nextUserMessageIndex(from) {
    for (let i = from + 1; i < this.messages.length; i++) {
      if (this.messages[i].role === 'user') return i
    }
    return null
  }

  _jumpToMessage(index) {
    this._jumping = true
    this.activeIndex = index
    this._scrollToMessage(this.messages[index])
    // Fallback: clear _jumping after 800ms for browsers that don't fire scrollend
    clearTimeout(this._jumpingTimeout)
    this._jumpingTimeout = setTimeout(() => {
      this._jumping = false
      this._updateActiveIndicator()
    }, 800)
  }

  _hideTooltip() {
    this.tooltipTarget.classList.add('hidden')
  }

  _scrollToMessage(msg, behavior = 'smooth') {
    if (!msg.element) return
    const containerRect = this.messagesContainer.getBoundingClientRect()
    const msgRect = msg.element.getBoundingClientRect()
    const targetScrollTop =
      msgRect.top -
      containerRect.top +
      this.messagesContainer.scrollTop -
      SCROLL_TOP_OFFSET
    this._hideTooltip()

    if (behavior === 'instant') {
      cancelAnimationFrame(this._scrollRafId)
      this.messagesContainer.scrollTo({
        top: targetScrollTop,
        behavior: 'instant',
      })
    } else {
      this._animateScrollTo(targetScrollTop)
    }
  }

  // Custom-duration smooth scroll. Native `behavior: 'smooth'` gives no control
  // over speed and is slower than we want for stepping between messages.
  _animateScrollTo(targetTop) {
    cancelAnimationFrame(this._scrollRafId)

    const startTop = this.messagesContainer.scrollTop
    const distance = targetTop - startTop
    if (distance === 0) return

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.messagesContainer.scrollTop = targetTop
      return
    }

    const startTime = performance.now()
    const step = (now) => {
      const t = Math.min(1, (now - startTime) / SCROLL_DURATION)
      // easeInOutQuad — matches the native smooth-scroll feel, just faster
      const eased = t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2
      this.messagesContainer.scrollTop = startTop + distance * eased
      if (t < 1) this._scrollRafId = requestAnimationFrame(step)
    }
    this._scrollRafId = requestAnimationFrame(step)
  }

  // Touch drag: tap-and-hold then drag to scrub through messages live (mobile)
  _handleTouchStart(event) {
    this._touchStartY = event.touches[0].clientY
    this._touchDragging = false
  }

  _handleTouchMove(event) {
    if (this._touchStartY == null) return
    const currentY = event.touches[0].clientY
    if (Math.abs(currentY - this._touchStartY) < DRAG_THRESHOLD) return

    if (!this._touchDragging) {
      this._touchDragging = true
      haptics.tick()
    }
    event.preventDefault()
    this._navigateByY(currentY)
  }

  _handleTouchEnd() {
    this._touchDragging = false
    this._touchStartY = null
  }

  // Mouse drag: click-and-drag to scrub through messages live (desktop equivalent of touch drag)
  _handleMouseDown(event) {
    if (event.button !== 0) return // left button only
    this._mouseStartY = event.clientY
    this._mouseDragging = false
    window.addEventListener('mousemove', this._boundMouseMove)
    window.addEventListener('mouseup', this._boundMouseUp)
  }

  _handleMouseMove(event) {
    if (this._mouseStartY == null) return
    const currentY = event.clientY
    if (Math.abs(currentY - this._mouseStartY) < DRAG_THRESHOLD) return

    this._mouseDragging = true
    event.preventDefault()
    this._navigateByY(currentY)
  }

  _handleMouseUp() {
    window.removeEventListener('mousemove', this._boundMouseMove)
    window.removeEventListener('mouseup', this._boundMouseUp)
    if (this._mouseDragging) {
      this._suppressClick = true
      // Clear after the current event-loop tick so the synchronous click that
      // fires right after mouseup (same indicator) is suppressed, but a drag
      // ending on empty rail or a different indicator doesn't leave the flag
      // stale for the next normal click.
      setTimeout(() => {
        this._suppressClick = false
      }, 0)
    }
    this._mouseDragging = false
    this._mouseStartY = null
  }

  _navigateByY(clientY) {
    const indicators = Array.from(
      this.railTarget.querySelectorAll('.js-chat-bot-thread-nav-indicator')
    )
    if (indicators.length === 0) return

    const railRect = this.railTarget.getBoundingClientRect()
    const y = clientY - railRect.top

    let closestIndex = 0
    let closestDist = Infinity
    indicators.forEach((ind, i) => {
      const center = parseFloat(ind.style.top) + INDICATOR_HEIGHT / 2
      const dist = Math.abs(y - center)
      if (dist < closestDist) {
        closestDist = dist
        closestIndex = i
      }
    })

    if (closestIndex === this.activeIndex) return
    this.activeIndex = closestIndex
    this._syncIndicators(closestIndex)
    this._scrollToMessage(this.messages[closestIndex], 'instant')
  }
}
